/**
 * Dashboard stats. Prefer nested GET /api/stats `today` when present.
 * Trunk charts use API `trunks` when that array is real; otherwise the same
 * buckets are derived from history rows. Destinations and CLI are never copied.
 *
 * `parseOperatorDesk` is the Stats page model. It reads the new desk shape
 * when present and the live `{today, all, series, trunks}` shape otherwise.
 * Missing fields stay null. It does not invent AMD, countries, or OTP steps.
 */

import { historyAmdFromRow } from "./historyOutcome";
import type { CallRecord, DayCount, TrunkStat } from "./types";

function startOfDay(date: Date): Date {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

function ymd(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function recordDay(record: CallRecord): string | null {
  if (!record.timestamp) return null;
  const date = new Date(record.timestamp);
  if (Number.isNaN(date.getTime())) return null;
  return ymd(date);
}

function amdKey(record: CallRecord): "HUMAN" | "MACHINE" | "SCREENING" | "UNKNOWN" {
  return historyAmdFromRow(record) || "UNKNOWN";
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function finite(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

export function isUnknownTrunk(id: string | null | undefined): boolean {
  const key = (id || "").trim().toLowerCase();
  return key === "" || key === "unknown";
}

function lastDays(now = new Date()): string[] {
  const days: string[] = [];
  for (let i = 6; i >= 0; i -= 1) {
    const day = new Date(startOfDay(now));
    day.setDate(day.getDate() - i);
    days.push(ymd(day));
  }
  return days;
}

function sortTrunks(rows: TrunkStat[]): TrunkStat[] {
  return [...rows].sort((a, b) => {
    const au = isUnknownTrunk(a.trunk_id) ? 1 : 0;
    const bu = isUnknownTrunk(b.trunk_id) ? 1 : 0;
    if (au !== bu) return au - bu;
    return b.calls - a.calls || a.trunk_id.localeCompare(b.trunk_id);
  });
}

/** Same buckets the stats API will emit. Blank trunk_id → unknown. */
export function deriveTrunks(records: CallRecord[]): TrunkStat[] {
  const days = lastDays();
  const map = new Map<string, TrunkStat & { durMs: number; durN: number }>();
  const bucket = (id: string) => {
    let row = map.get(id);
    if (!row) {
      row = {
        trunk_id: id,
        calls: 0,
        human: 0,
        machine: 0,
        screening: 0,
        unknown: 0,
        matched: 0,
        connect_rate: 0,
        avg_duration_s: 0,
        series: days.map((date) => ({ date, calls: 0, human: 0, machine: 0 })),
        durMs: 0,
        durN: 0,
      };
      map.set(id, row);
    }
    return row;
  };
  for (const record of records) {
    const id = (record.trunk_id || "").trim() || "unknown";
    const row = bucket(id);
    row.calls += 1;
    const amd = amdKey(record);
    if (amd === "HUMAN") row.human += 1;
    else if (amd === "MACHINE") row.machine += 1;
    else if (amd === "SCREENING") row.screening += 1;
    else row.unknown += 1;
    if (record.final_match === true) row.matched += 1;
    if (typeof record.duration_ms === "number" && record.duration_ms >= 0) {
      row.durMs += record.duration_ms;
      row.durN += 1;
    }
    const day = recordDay(record);
    const slot = day ? row.series.find((item) => item.date === day) : undefined;
    if (slot) {
      slot.calls += 1;
      if (amd === "HUMAN") slot.human = (slot.human ?? 0) + 1;
      if (amd === "MACHINE") slot.machine = (slot.machine ?? 0) + 1;
    }
  }
  const out: TrunkStat[] = [];
  for (const row of map.values()) {
    const { durMs, durN, ...rest } = row;
    rest.connect_rate = rest.calls ? rest.human / rest.calls : 0;
    rest.avg_duration_s = durN ? Math.round((durMs / durN / 1000) * 1000) / 1000 : 0;
    out.push(rest);
  }
  return sortTrunks(out);
}

function parseSeries(raw: unknown, fallback?: DayCount[]): DayCount[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const byDate = new Map((fallback ?? []).map((row) => [row.date, row]));
  const out: DayCount[] = [];
  for (const item of raw) {
    const rec = asRecord(item);
    if (!rec) continue;
    const date =
      typeof rec.date === "string" ? rec.date : typeof rec.day === "string" ? rec.day : "";
    if (!date) continue;
    const prior = byDate.get(date);
    const human = finite(rec.human);
    const machine = finite(rec.machine);
    out.push({
      date,
      calls: finite(rec.calls) ?? prior?.calls ?? 0,
      human: human ?? prior?.human ?? 0,
      machine: machine ?? prior?.machine ?? 0,
    });
  }
  return out.length ? out : null;
}

function parseTrunk(raw: unknown): TrunkStat | null {
  const rec = asRecord(raw);
  if (!rec) return null;
  const trunkId = String(rec.trunk_id ?? rec.id ?? "").trim() || "unknown";
  const calls = finite(rec.calls) ?? 0;
  const human = finite(rec.human) ?? 0;
  const machine = finite(rec.machine) ?? 0;
  const screening = finite(rec.screening) ?? 0;
  const unknown = finite(rec.unknown) ?? 0;
  const matched = finite(rec.matched) ?? 0;
  const connect = finite(rec.connect_rate);
  const avg = finite(rec.avg_duration_s) ?? 0;
  return {
    trunk_id: trunkId,
    calls,
    human,
    machine,
    screening,
    unknown,
    matched,
    connect_rate: connect ?? (calls ? human / calls : 0),
    avg_duration_s: avg,
    series: parseSeries(rec.series) ?? [],
  };
}

function parseTrunkList(raw: unknown): TrunkStat[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.map(parseTrunk).filter((row): row is TrunkStat => row !== null);
  }
  const rec = asRecord(raw);
  if (!rec) return [];
  if (Array.isArray(rec.trunks)) return parseTrunkList(rec.trunks);
  if (typeof rec.trunk_id === "string" || typeof rec.id === "string") {
    const one = parseTrunk(rec);
    return one ? [one] : [];
  }
  const mapped: TrunkStat[] = [];
  for (const [id, value] of Object.entries(rec)) {
    const child = asRecord(value);
    if (!child) continue;
    const parsed = parseTrunk({ trunk_id: child.trunk_id ?? child.id ?? id, ...child });
    if (parsed) mapped.push(parsed);
  }
  return mapped;
}

/**
 * Prefer a non-empty API trunk list. If that list is only `unknown` and
 * history rows carry real sofia ids, use the history buckets instead.
 */
export function resolveTrunks(raw: unknown, records: CallRecord[], fallback: TrunkStat[] = []): TrunkStat[] {
  const parsed = parseTrunkList(raw);
  const derived = records.length ? deriveTrunks(records) : fallback;
  const apiOnlyUnknown = parsed.length > 0 && parsed.every((row) => isUnknownTrunk(row.trunk_id));
  const historyReal = records.some((row) => !isUnknownTrunk(row.trunk_id));
  if (parsed.length > 0 && !(apiOnlyUnknown && historyReal)) return sortTrunks(parsed);
  return sortTrunks(derived);
}

export interface DeskCounts {
  human: number | null;
  machine: number | null;
  screening: number | null;
  unknown: number | null;
}

export interface DeskSummary extends DeskCounts {
  calls: number | null;
  /** 0–1 human ASR. Null when the payload did not send a rate or both counts. */
  asr: number | null;
  avg_handle_ms: number | null;
  otp_verified: number | null;
}

export interface DeskSeriesPoint extends DeskCounts {
  date: string;
  calls: number | null;
  asr: number | null;
}

export interface DeskTrunk {
  trunk_id: string;
  calls: number | null;
  human: number | null;
  machine: number | null;
  screening: number | null;
  asr: number | null;
  avg_duration_ms: number | null;
}

export interface DeskCountry {
  country_iso2: string;
  calls: number | null;
  human: number | null;
  machine: number | null;
  screening: number | null;
  asr: number | null;
}

export interface DeskOutcome {
  outcome: string;
  count: number | null;
}

export interface DeskOtp {
  prompted: number | null;
  sent: number | null;
  collected: number | null;
  match: number | null;
  mismatch: number | null;
  timeout: number | null;
}

export type DeskWindowId = "today" | "d7" | "d30";

export interface OperatorDesk {
  summary: DeskSummary;
  /** Nested summary windows. Null when that object was absent — zeros stay zeros. */
  windows: Record<DeskWindowId, DeskSummary | null>;
  series: DeskSeriesPoint[];
  byTrunk: DeskTrunk[];
  byCountry: DeskCountry[];
  outcomes: DeskOutcome[];
  otp: DeskOtp;
  amd: {
    today: DeskCounts | null;
    d7: DeskCounts | null;
    d30: DeskCounts | null;
  };
  /** True when that key was on the payload. Empty arrays still count as present. */
  present: {
    summary: boolean;
    series: boolean;
    trunks: boolean;
    countries: boolean;
    outcomes: boolean;
    otp: boolean;
    amdMix: boolean;
  };
  /** KPI came from legacy today/all, not the new `summary` object. */
  legacyWindow: boolean;
}

const EMPTY_COUNTS: DeskCounts = {
  human: null,
  machine: null,
  screening: null,
  unknown: null,
};

const EMPTY_OTP: DeskOtp = {
  prompted: null,
  sent: null,
  collected: null,
  match: null,
  mismatch: null,
  timeout: null,
};

function emptySummary(): DeskSummary {
  return {
    ...EMPTY_COUNTS,
    calls: null,
    asr: null,
    avg_handle_ms: null,
    otp_verified: null,
  };
}

function asFraction(rate: number | null, human: number | null, calls: number | null): number | null {
  if (rate !== null) {
    if (rate > 1 || rate < -1) return rate / 100;
    return rate;
  }
  if (human !== null && calls !== null) {
    if (calls > 0) return human / calls;
    if (human === 0) return 0;
  }
  return null;
}

function secondsToMs(seconds: number | null): number | null {
  if (seconds === null) return null;
  return Math.round(seconds * 1000);
}

function readCounts(rec: Record<string, unknown>): DeskCounts {
  return {
    human: finite(rec.human ?? rec.HUMAN),
    machine: finite(rec.machine ?? rec.MACHINE ?? rec.vm ?? rec.VM),
    screening: finite(rec.screening ?? rec.SCREENING),
    unknown: finite(rec.unknown ?? rec.UNKNOWN),
  };
}

function countsPresent(counts: DeskCounts): boolean {
  return counts.human !== null || counts.machine !== null || counts.screening !== null || counts.unknown !== null;
}

function readSummary(rec: Record<string, unknown>): DeskSummary {
  const calls = finite(rec.total_calls ?? rec.calls ?? rec.calls_today);
  const counts = readCounts(rec);
  const human = finite(rec.human_connects) ?? counts.human;
  const screening = finite(rec.screening_only) ?? counts.screening;
  const asr = asFraction(finite(rec.asr ?? rec.connect_rate ?? rec.human_asr), human, calls);
  const handleDirect = finite(rec.avg_handle_ms ?? rec.avg_duration_ms);
  const handle =
    handleDirect !== null
      ? Math.round(handleDirect)
      : secondsToMs(finite(rec.avg_handle_s ?? rec.avg_duration_s));
  return {
    calls,
    human,
    machine: counts.machine,
    screening,
    unknown: counts.unknown,
    asr,
    avg_handle_ms: handle,
    otp_verified: finite(rec.otp_verified ?? rec.otp_match ?? rec.matched),
  };
}

function readWindow(raw: unknown): DeskSummary | null {
  const rec = asRecord(raw);
  if (!rec) return null;
  if (!("calls" in rec) && !("human_connects" in rec) && !("human" in rec)) return null;
  return readSummary(rec);
}

function hasSummarySignal(rec: Record<string, unknown>): boolean {
  return (
    "calls" in rec ||
    "total_calls" in rec ||
    "human" in rec ||
    "machine" in rec ||
    "connect_rate" in rec ||
    "asr" in rec ||
    "matched" in rec ||
    "avg_duration_s" in rec ||
    "avg_handle_ms" in rec
  );
}

function unsafeToken(value: string): boolean {
  const text = value.trim();
  if (!text || text.length > 48) return true;
  if (/[\\/]/.test(text) || text.includes("..")) return true;
  if (/\d{5,}/.test(text) || text.startsWith("+")) return true;
  if (/password|secret|token|recording/i.test(text)) return true;
  return false;
}

function parseSeriesPoint(raw: unknown): DeskSeriesPoint | null {
  const rec = asRecord(raw);
  if (!rec) return null;
  const date = typeof rec.date === "string" ? rec.date : typeof rec.day === "string" ? rec.day : "";
  if (!date || unsafeToken(date)) return null;
  const counts = readCounts(rec);
  const calls = finite(rec.calls);
  return {
    date,
    calls,
    ...counts,
    asr: asFraction(finite(rec.asr ?? rec.connect_rate), counts.human, calls),
  };
}

function sortSeries(rows: DeskSeriesPoint[]): DeskSeriesPoint[] {
  const dated = rows.every((row) => /^\d{4}-\d{2}-\d{2}/.test(row.date));
  const ordered = dated ? [...rows].sort((a, b) => a.date.localeCompare(b.date)) : rows;
  return ordered.slice(-30);
}

function parseTrunkRow(raw: unknown, fallbackId?: string): DeskTrunk | null {
  const rec = asRecord(raw);
  if (!rec) return null;
  const trunkId = String(rec.trunk_id ?? rec.id ?? rec.trunk ?? fallbackId ?? "").trim() || "unknown";
  if (unsafeToken(trunkId) && trunkId.toLowerCase() !== "unknown") return null;
  const calls = finite(rec.calls);
  const human = finite(rec.human ?? rec.HUMAN);
  return {
    trunk_id: trunkId,
    calls,
    human,
    machine: finite(rec.machine ?? rec.MACHINE ?? rec.vm),
    screening: finite(rec.screening ?? rec.SCREENING),
    asr: asFraction(finite(rec.asr ?? rec.connect_rate ?? rec.human_asr), human, calls),
    avg_duration_ms:
      finite(rec.avg_duration_ms ?? rec.avg_handle_ms) !== null
        ? Math.round(finite(rec.avg_duration_ms ?? rec.avg_handle_ms) as number)
        : secondsToMs(finite(rec.avg_duration_s)),
  };
}

function parseNamedList(raw: unknown, idKeys: string[]): Record<string, unknown>[] {
  if (Array.isArray(raw)) {
    return raw.map((item) => asRecord(item)).filter((item): item is Record<string, unknown> => item !== null);
  }
  const rec = asRecord(raw);
  if (!rec) return [];
  if (idKeys.some((key) => typeof rec[key] === "string")) return [rec];
  const rows: Record<string, unknown>[] = [];
  for (const [id, value] of Object.entries(rec)) {
    const child = asRecord(value);
    if (!child) continue;
    rows.push({ ...child, [idKeys[0]]: child[idKeys[0]] ?? id });
  }
  return rows;
}

function parseCountryRow(raw: Record<string, unknown>): DeskCountry | null {
  const rawIso = String(raw.country_iso2 ?? raw.iso2 ?? raw.country ?? "").trim();
  const upper = rawIso.toUpperCase();
  const iso = upper === "UNKNOWN" ? "unknown" : upper;
  if (iso !== "unknown" && !/^[A-Z]{2}$/.test(iso)) return null;
  const calls = finite(raw.calls);
  const human = finite(raw.human ?? raw.HUMAN);
  return {
    country_iso2: iso === "unknown" ? "unknown" : iso,
    calls,
    human,
    machine: finite(raw.machine ?? raw.MACHINE ?? raw.vm),
    screening: finite(raw.screening ?? raw.SCREENING),
    asr: asFraction(finite(raw.asr ?? raw.connect_rate ?? raw.human_asr), human, calls),
  };
}

function byCalls(a: { calls: number | null }, b: { calls: number | null }, tie: string, tieB: string): number {
  return (b.calls ?? -1) - (a.calls ?? -1) || tie.localeCompare(tieB);
}

function parseOutcomes(raw: unknown): DeskOutcome[] {
  const rows: DeskOutcome[] = [];
  if (Array.isArray(raw)) {
    for (const item of raw) {
      const rec = asRecord(item);
      if (!rec) continue;
      const outcome = String(rec.outcome ?? rec.name ?? rec.label ?? "").trim();
      if (!outcome || unsafeToken(outcome)) continue;
      rows.push({ outcome, count: finite(rec.count ?? rec.n ?? rec.calls) });
    }
    return rows;
  }
  const rec = asRecord(raw);
  if (!rec) return [];
  for (const [key, value] of Object.entries(rec)) {
    if (unsafeToken(key)) continue;
    const child = asRecord(value);
    const count = child ? finite(child.count ?? child.n ?? child.calls) : finite(value);
    rows.push({ outcome: key, count });
  }
  return rows;
}

function parseOtp(raw: unknown): DeskOtp {
  const rec = asRecord(raw);
  if (!rec) return { ...EMPTY_OTP };
  return {
    prompted: finite(rec.prompted),
    sent: finite(rec.sent ?? rec.otp_sent),
    collected: finite(rec.collected ?? rec.collecting),
    match: finite(rec.match ?? rec.matched ?? rec.verified),
    mismatch: finite(rec.mismatch ?? rec.unmatched),
    timeout: finite(rec.timeout),
  };
}

function parseAmdWindows(raw: unknown): OperatorDesk["amd"] | null {
  const rec = asRecord(raw);
  if (!rec) return null;
  const nested =
    "today" in rec ||
    "d7" in rec ||
    "d30" in rec ||
    "7d" in rec ||
    "30d" in rec ||
    "week" in rec ||
    "month" in rec;
  if (!nested) {
    const flat = readCounts(rec);
    return countsPresent(flat) ? { today: flat, d7: null, d30: null } : null;
  }
  const today = readCounts(asRecord(rec.today) ?? {});
  const d7 = readCounts(asRecord(rec.d7 ?? rec["7d"] ?? rec.week) ?? {});
  const d30 = readCounts(asRecord(rec.d30 ?? rec["30d"] ?? rec.month) ?? {});
  return {
    today: countsPresent(today) ? today : null,
    d7: countsPresent(d7) ? d7 : null,
    d30: countsPresent(d30) ? d30 : null,
  };
}

function firstPresent(body: Record<string, unknown>, keys: string[]): unknown {
  for (const key of keys) {
    if (key in body) return body[key];
  }
  return undefined;
}

/**
 * Normalize GET /api/stats for the operator desk.
 * Stored AMD counts only — this does not classify. Blank sections stay blank.
 */
function outcomePayload(raw: unknown): unknown {
  const rec = asRecord(raw);
  if (!rec || !("history_outcome" in rec)) return raw;
  return rec.history_outcome;
}

export function parseOperatorDesk(incoming: unknown): OperatorDesk {
  const body = asRecord(incoming);
  const summaryRec = asRecord(body?.summary);
  const windows: Record<DeskWindowId, DeskSummary | null> = {
    today: readWindow(summaryRec?.today),
    d7: readWindow(summaryRec?.d7 ?? summaryRec?.["7d"]),
    d30: readWindow(summaryRec?.d30 ?? summaryRec?.["30d"]),
  };
  const nested = windows.d30 ?? windows.d7 ?? windows.today;
  const flatSummary =
    !nested && summaryRec && hasSummarySignal(summaryRec) ? readSummary(summaryRec) : null;
  const legacyBucket = asRecord(body?.all) ?? asRecord(body?.today);
  const summary =
    nested ??
    flatSummary ??
    (legacyBucket && hasSummarySignal(legacyBucket)
      ? readSummary(legacyBucket)
      : body && hasSummarySignal(body)
        ? readSummary(body)
        : emptySummary());

  const seriesRaw = body?.series;
  const series = Array.isArray(seriesRaw)
    ? sortSeries(seriesRaw.map(parseSeriesPoint).filter((row): row is DeskSeriesPoint => row !== null))
    : [];

  const trunkRaw = firstPresent(body ?? {}, ["by_trunk", "trunks", "trunk"]);
  const byTrunk = parseNamedList(trunkRaw, ["trunk_id", "id", "trunk"])
    .map((row) => parseTrunkRow(row))
    .filter((row): row is DeskTrunk => row !== null)
    .sort((a, b) => byCalls(a, b, a.trunk_id, b.trunk_id));

  const countryRaw = firstPresent(body ?? {}, ["by_country", "countries", "country"]);
  const byCountry = parseNamedList(countryRaw, ["country_iso2", "iso2", "country"])
    .map(parseCountryRow)
    .filter((row): row is DeskCountry => row !== null)
    .sort((a, b) => byCalls(a, b, a.country_iso2, b.country_iso2));

  const outcomeRaw = firstPresent(body ?? {}, ["outcomes", "outcome_mix"]);
  const outcomes = parseOutcomes(outcomePayload(outcomeRaw)).sort((a, b) => byCalls(
    { calls: a.count },
    { calls: b.count },
    a.outcome,
    b.outcome,
  ));

  const otpRaw = firstPresent(body ?? {}, ["otp_funnel", "otp"]);
  const amdParsed = parseAmdWindows(body?.amd_mix ?? body?.amd);
  const legacyAmd = !amdParsed ? readCounts(asRecord(body?.today) ?? {}) : null;

  return {
    summary,
    windows,
    series,
    byTrunk,
    byCountry,
    outcomes,
    otp: otpRaw === undefined ? { ...EMPTY_OTP } : parseOtp(otpRaw),
    amd: amdParsed ?? {
      today: legacyAmd && countsPresent(legacyAmd) ? legacyAmd : null,
      d7: null,
      d30: null,
    },
    present: {
      summary: Boolean(nested || flatSummary || (legacyBucket && hasSummarySignal(legacyBucket))),
      series: Array.isArray(seriesRaw),
      trunks: trunkRaw !== undefined,
      countries: countryRaw !== undefined,
      outcomes: outcomeRaw !== undefined,
      otp: otpRaw !== undefined,
      amdMix: body?.amd_mix !== undefined || body?.amd !== undefined,
    },
    legacyWindow: !nested && !flatSummary && Boolean(legacyBucket && hasSummarySignal(legacyBucket)),
  };
}
